(() => {
  const SELECTOR = '[data-kzh-root]';
  const GLOBAL_KEY = 'KizilcoHomePremium';

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].initAll === 'function') {
    window[GLOBAL_KEY].initAll();
    return;
  }

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function initKizilcoHome(root) {
    if (!root || root.dataset.kzhInitialized === 'true') return;
    root.dataset.kzhInitialized = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const cleanups = [];

    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const cards = Array.from(root.querySelectorAll('[data-kzh-card]'));
    const revealItems = Array.from(root.querySelectorAll('[data-kzh-reveal]'))
      .filter((item) => !item.matches('[data-kzh-card]'));

    if (!reduceMotion && 'IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

      revealItems.forEach((item) => revealObserver.observe(item));
      cleanups.push(() => revealObserver.disconnect());
    } else {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    }

    if (!reduceMotion && cards.length && 'IntersectionObserver' in window) {
      const prewarmDistance = clamp(window.innerHeight * 0.55, 320, 600);

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        card.classList.toggle(
          'is-animation-active',
          rect.bottom > -prewarmDistance && rect.top < window.innerHeight + prewarmDistance
        );
      });

      root.classList.add('kzh--animation-gated');
      const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-animation-active', entry.isIntersecting);
        });
      }, { rootMargin: `${Math.round(prewarmDistance)}px 0px`, threshold: 0 });

      cards.forEach((card) => animationObserver.observe(card));
      cleanups.push(() => {
        animationObserver.disconnect();
        root.classList.remove('kzh--animation-gated');
      });
    } else {
      cards.forEach((card) => card.classList.add('is-animation-active'));
    }

    if (!reduceMotion && finePointer) {
      root.querySelectorAll('[data-kzh-parallax-zone]').forEach((zone) => {
        const layers = Array.from(zone.querySelectorAll('[data-kzh-depth]'));
        const cursor = zone.querySelector('[data-kzh-cursor]');
        const state = { x: 0, y: 0, tx: 0, ty: 0, frame: 0 };

        const render = () => {
          state.x += (state.tx - state.x) * 0.105;
          state.y += (state.ty - state.y) * 0.105;

          layers.forEach((layer) => {
            const depth = Number(layer.dataset.kzhDepth || 0.2);
            layer.style.setProperty('--kzh-depth-x', `${state.x * depth * 32}px`);
            layer.style.setProperty('--kzh-depth-y', `${state.y * depth * 32}px`);
          });

          if (Math.abs(state.tx - state.x) > 0.002 || Math.abs(state.ty - state.y) > 0.002) {
            state.frame = requestAnimationFrame(render);
          } else {
            state.frame = 0;
          }
        };

        const schedule = () => {
          if (!state.frame) state.frame = requestAnimationFrame(render);
        };

        const onMove = (event) => {
          const rect = zone.getBoundingClientRect();
          state.tx = clamp((event.clientX - rect.left) / rect.width - 0.5, -0.5, 0.5) * 2;
          state.ty = clamp((event.clientY - rect.top) / rect.height - 0.5, -0.5, 0.5) * 2;
          if (cursor) {
            cursor.style.setProperty('--kzh-cursor-x', `${event.clientX - rect.left}px`);
            cursor.style.setProperty('--kzh-cursor-y', `${event.clientY - rect.top}px`);
            cursor.style.setProperty('--kzh-cursor-opacity', '1');
          }
          schedule();
        };

        const onLeave = () => {
          state.tx = 0;
          state.ty = 0;
          if (cursor) cursor.style.setProperty('--kzh-cursor-opacity', '0');
          schedule();
        };

        listen(zone, 'pointermove', onMove, { passive: true });
        listen(zone, 'pointerleave', onLeave, { passive: true });
        cleanups.push(() => {
          if (state.frame) cancelAnimationFrame(state.frame);
        });
      });
    }

    const cardControllers = new Map();
    let activeTouch = null;

    cards.forEach((card) => {
      const aiHanger = card.querySelector('[data-kzh-ai-hanger]');

      const updateCardFromPoint = (event, intensity = 1) => {
        const rect = card.getBoundingClientRect();
        const x = clamp((event.clientX - rect.left) / Math.max(rect.width, 1), 0, 1);
        const y = clamp((event.clientY - rect.top) / Math.max(rect.height, 1), 0, 1);
        card.style.setProperty('--kzh-card-rx', `${(0.5 - y) * 3.8 * intensity}deg`);
        card.style.setProperty('--kzh-card-ry', `${(x - 0.5) * 4.8 * intensity}deg`);
        card.style.setProperty('--kzh-glow-x', `${x * 100}%`);
        card.style.setProperty('--kzh-glow-y', `${y * 100}%`);

        if (aiHanger) {
          const nx = (x - 0.5) * 2;
          const ny = (y - 0.5) * 2;
          aiHanger.style.setProperty('--kzh-ai-drift-x', `${nx * 9 * intensity}px`);
          aiHanger.style.setProperty('--kzh-ai-drift-y', `${ny * 5 * intensity}px`);
          aiHanger.style.setProperty('--kzh-ai-depth-rx', `${ny * -5 * intensity}deg`);
          aiHanger.style.setProperty('--kzh-ai-depth-ry', `${nx * 8 * intensity}deg`);
        }
      };

      const resetCard = () => {
        card.style.setProperty('--kzh-card-rx', '0deg');
        card.style.setProperty('--kzh-card-ry', '0deg');
        if (aiHanger) {
          aiHanger.style.removeProperty('--kzh-ai-drift-x');
          aiHanger.style.removeProperty('--kzh-ai-drift-y');
          aiHanger.style.removeProperty('--kzh-ai-depth-rx');
          aiHanger.style.removeProperty('--kzh-ai-depth-ry');
        }
      };

      const onFinePointerMove = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        updateCardFromPoint(event);
      };

      const activateCard = (point) => {
        card.classList.add('is-touch-active', 'is-animation-active');
        updateCardFromPoint(point);
      };

      const deactivateCard = () => {
        card.classList.remove('is-touch-active');
        resetCard();
      };

      cardControllers.set(card, { activateCard, deactivateCard, updateCardFromPoint });

      if (!reduceMotion && finePointer) {
        listen(card, 'pointerenter', onFinePointerMove, { passive: true });
        listen(card, 'pointermove', onFinePointerMove, { passive: true });
        listen(card, 'pointerleave', resetCard, { passive: true });
      }

      listen(card, 'blur', deactivateCard, true);
      cleanups.push(() => {
        card.classList.remove('is-touch-active');
      });
    });

    if (cards.length) {
      const findTouch = (touchList, identifier) => {
        for (let index = 0; index < touchList.length; index += 1) {
          if (touchList[index].identifier === identifier) return touchList[index];
        }
        return null;
      };

      const onCardTouchStart = (event) => {
        if (activeTouch || !event.changedTouches.length) return;
        const target = event.target && event.target.closest
          ? event.target.closest('[data-kzh-card]')
          : null;
        if (!target || !root.contains(target)) return;

        const controller = cardControllers.get(target);
        if (!controller) return;
        const touch = event.changedTouches[0];
        activeTouch = { identifier: touch.identifier, card: target };
        controller.activateCard(touch);
      };

      const onCardTouchMove = (event) => {
        if (!activeTouch) return;
        const touch = findTouch(event.touches, activeTouch.identifier);
        if (!touch) return;
        const controller = cardControllers.get(activeTouch.card);
        if (controller) controller.updateCardFromPoint(touch);
      };

      const onCardTouchFinish = (event) => {
        if (!activeTouch) return;
        const finishedTouch = findTouch(event.changedTouches, activeTouch.identifier);
        if (!finishedTouch) return;
        const controller = cardControllers.get(activeTouch.card);
        if (controller) controller.deactivateCard();
        activeTouch = null;
      };

      listen(root, 'touchstart', onCardTouchStart, { passive: true });
      listen(root, 'touchmove', onCardTouchMove, { passive: true });
      listen(root, 'touchend', onCardTouchFinish, { passive: true });
      listen(root, 'touchcancel', onCardTouchFinish, { passive: true });
      cleanups.push(() => {
        if (!activeTouch) return;
        const controller = cardControllers.get(activeTouch.card);
        if (controller) controller.deactivateCard();
        activeTouch = null;
      });
    }

    if (!reduceMotion) {
      const hero = root.querySelector('[data-kzh-hero]');
      const heroMedia = root.querySelector('.kzh-hero__media');
      const heroGrid = root.querySelector('.kzh-hero__grid');
      const categoryStage = root.querySelector('[data-kzh-category-stage]');
      const process = root.querySelector('[data-kzh-process]');
      const processProgress = root.querySelector('[data-kzh-process-progress]');
      const processRoute = root.querySelector('[data-kzh-process-route]');
      const processRail = root.querySelector('[data-kzh-process-rail]');
      const processRocket = root.querySelector('[data-kzh-process-rocket]');
      const processSteps = Array.from(root.querySelectorAll('[data-kzh-process-stage]'));
      let scrollFrame = 0;
      let cachedPlumeWidth = 0;
      let processRegion = '';
      let previousScrollY = Math.max(window.scrollY, 0);
      let scrollDirection = 1;

      const updateScrollEffects = () => {
        scrollFrame = 0;
        const scrollY = Math.max(window.scrollY, 0);
        if (scrollY !== previousScrollY) {
          scrollDirection = scrollY > previousScrollY ? 1 : -1;
          previousScrollY = scrollY;
        }
        const viewportHeight = Math.max(window.innerHeight, 1);

        if (hero && heroMedia && heroGrid) {
          const heroRect = hero.getBoundingClientRect();
          const progress = clamp(-heroRect.top / Math.max(heroRect.height, 1), 0, 1);
          let heroCovered = false;

          if (categoryStage) {
            const stageRect = categoryStage.getBoundingClientRect();
            const coverProgress = clamp(
              (heroRect.bottom - 100 - stageRect.top) / Math.max(heroRect.height - 100, 1),
              0,
              1
            );
            hero.style.setProperty('--kzh-card-scroll-overlay', `${coverProgress * 0.72}`);
            heroCovered = coverProgress > 0.985;
          }

          root.classList.toggle('kzh--hero-covered', heroCovered);
          if (!heroCovered) {
            heroMedia.style.setProperty('--kzh-depth-y', `${progress * 32}px`);
            heroGrid.style.setProperty('--kzh-depth-y', `${progress * -18}px`);
          }
        }

        if (process && processProgress && processRoute && processRail && processRocket && processSteps.length) {
          const processRect = process.getBoundingClientRect();
          const beforeProcess = processRect.top > viewportHeight * 1.45;
          const afterProcess = processRect.bottom < -viewportHeight * 0.2;

          if (beforeProcess || afterProcess) {
            const nextRegion = beforeProcess ? 'before' : 'after';
            if (processRegion !== nextRegion) {
              const completed = nextRegion === 'after';
              process.style.setProperty('--kzh-process-progress-ratio', completed ? '1' : '0');
              process.style.setProperty('--kzh-process-rocket-position', '0px');
              process.style.setProperty('--kzh-process-rocket-opacity', '0');
              processSteps.forEach((step) => step.classList.toggle('is-passed', completed));
              processRegion = nextRegion;
            }
            return;
          }

          processRegion = 'active';
          const routeRect = processRoute.getBoundingClientRect();
          const railRect = processRail.getBoundingClientRect();
          const lastStepRect = processSteps[processSteps.length - 1].getBoundingClientRect();
          const verticalRoute = railRect.height > railRect.width;
          const railStart = verticalRoute ? railRect.top : railRect.left;
          const railLength = Math.max(verticalRoute ? railRect.height : railRect.width, 1);
          let rocketPosition = 0;
          let rocketOpacity = 0;
          let trailProgress = 0;

          if (verticalRoute) {
            const launchStep = processSteps[1] || processSteps[0];
            const launchStepRect = launchStep.getBoundingClientRect();
            const launchLine = viewportHeight * 0.82;
            const launchTravel = launchLine - launchStepRect.top;
            const naturalFlightDistance = Math.max(lastStepRect.top - launchStepRect.top, 1);
            const flightDistance = Math.max(
              naturalFlightDistance + viewportHeight * 0.12,
              viewportHeight * 0.52
            );
            const linearFlight = clamp(launchTravel / flightDistance, 0, 1);
            const acceleratedFlight = linearFlight > 0
              ? 1 - Math.pow(1 - linearFlight, 1.65)
              : 0;
            const rocketTravel = railLength + processRocket.offsetWidth * 0.7;
            rocketPosition = acceleratedFlight * rocketTravel;
            trailProgress = clamp(rocketPosition / railLength, 0, 1);
            rocketOpacity = linearFlight <= 0
              ? 0
              : clamp(linearFlight / 0.04, 0, 1) * clamp((1 - linearFlight) / 0.055, 0, 1);
          } else {
            const startLine = viewportHeight * 0.79;
            const finishLine = viewportHeight * 0.35;
            const scrollTravel = startLine - routeRect.top;
            const scrollDistance = Math.max(startLine - finishLine, 1);
            const progress = clamp(scrollTravel / scrollDistance, 0, 1);
            const trackTravel = Math.max(railLength - processRocket.offsetWidth, 0);
            const exitDistance = Math.max(viewportHeight * 0.22, 160);
            const exitProgress = clamp((scrollTravel - scrollDistance) / exitDistance, 0, 1);
            if (!cachedPlumeWidth) {
              const plume = processRocket.querySelector('.kzh-process__rocket-plume');
              cachedPlumeWidth = plume ? plume.offsetWidth : 320;
            }
            const exitTravel = cachedPlumeWidth + processRocket.offsetWidth * 1.5;
            rocketPosition = progress * trackTravel + exitProgress * exitTravel;
            rocketOpacity = clamp((1 - exitProgress) / 0.08, 0, 1);
            trailProgress = progress;
          }

          process.style.setProperty('--kzh-process-progress-ratio', `${trailProgress}`);
          process.style.setProperty('--kzh-process-rocket-position', `${rocketPosition}px`);
          process.style.setProperty('--kzh-process-rocket-opacity', `${rocketOpacity}`);
          process.style.setProperty('--kzh-process-rocket-facing', `${scrollDirection}`);

          processSteps.forEach((step) => {
            const marker = step.querySelector('.kzh-process__number');
            if (!marker) return;
            const markerRect = marker.getBoundingClientRect();
            const stepRect = step.getBoundingClientRect();
            const markerCenter = verticalRoute
              ? markerRect.top + markerRect.height / 2
              : stepRect.left;
            const markerProgress = clamp((markerCenter - railStart) / railLength, 0, 1);
            step.classList.toggle('is-passed', trailProgress + 0.012 >= markerProgress);
          });
        }
      };

      const requestScrollUpdate = () => {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateScrollEffects);
      };

      listen(window, 'scroll', requestScrollUpdate, { passive: true });
      listen(window, 'resize', () => {
        cachedPlumeWidth = 0;
        processRegion = '';
        requestScrollUpdate();
      }, { passive: true });
      updateScrollEffects();
      cleanups.push(() => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        root.classList.remove('kzh--hero-covered');
      });
    }

    root.querySelectorAll('[data-kzh-builder-link]').forEach((link) => {
      listen(link, 'click', (event) => {
        const builder = document.querySelector('[data-kz-project-builder]');
        if (!builder) return;
        event.preventDefault();
        builder.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });

    root._kzhCleanup = () => {
      cleanups.forEach((cleanup) => cleanup());
      delete root._kzhCleanup;
      delete root.dataset.kzhInitialized;
    };
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(SELECTOR)) initKizilcoHome(scope);
    scope.querySelectorAll && scope.querySelectorAll(SELECTOR).forEach(initKizilcoHome);
  }

  window[GLOBAL_KEY] = { initAll };
  initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches(SELECTOR)
      ? event.target
      : event.target.querySelector && event.target.querySelector(SELECTOR);
    if (root && root._kzhCleanup) root._kzhCleanup();
  });
})();
